import {getBackendErrorMessage} from '../../shared/utils/httpError';
import {extractListPayload} from '../../shared/utils/payload';

export {extractListPayload};

export const getBackendMessage = (error, fallbackMessage) => getBackendErrorMessage(error, fallbackMessage);

// Só três tipos no identity (BBF-181). Pelo portal, o cliente cria sempre USER; quem administra a
// assinatura é o papel no vínculo, escolhido por licença.
export const PORTAL_ACCESS_TYPE = 'USER';

export const normalizeReferenceId = (item) => item?.id || item?.roleId || item?.licenseId || '';

// Vínculo da conta com cada assinatura (BBF-67): papel, situação e validade valem só nela.
export const PAPEL_NO_VINCULO_OPTIONS = [
    {value: 'USUARIO', label: 'Usuário'},
    {value: 'ADMINISTRADOR', label: 'Administrador'},
];

const EQUIPE_BUSIFARM = ['ADMIN', 'SUPPORT'];

/**
 * Se quem está logado pode dar o papel de Administrador nesta licença. É a regra do identity: só
 * um Administrador da assinatura, com o vínculo valendo, ou a equipe Busifarm. Sem saber quem é
 * (o /me falhou), não oferece; o backend recusaria de todo jeito.
 */
export const podeConcederAdministrador = (me, licenseId) => {
    if (!me || !licenseId) return false;
    if (EQUIPE_BUSIFARM.includes(me.specialAccessType)) return true;
    return (me.vinculos || []).some((vinculo) => vinculo?.licenseId === licenseId
        && vinculo?.papel === 'ADMINISTRADOR'
        && vinculo?.vale === true);
};

/** Papéis que a tela oferece numa licença para quem está logado. */
export const papelOptionsPara = (me, licenseId) => (
    podeConcederAdministrador(me, licenseId)
        ? PAPEL_NO_VINCULO_OPTIONS
        : PAPEL_NO_VINCULO_OPTIONS.filter((option) => option.value !== 'ADMINISTRADOR')
);

const SITUACAO_LABEL = {ATIVO: 'Ativo', DESATIVADO: 'Desativado', BLOQUEADO: 'Bloqueado'};

const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('pt-BR');
};

/** Rótulo de um vínculo para a lista: papel, situação e, se houver, a validade. */
export const vinculoLabel = (vinculo) => {
    const papel = PAPEL_NO_VINCULO_OPTIONS.find((option) => option.value === vinculo?.papel)?.label || 'Usuário';
    const situacao = SITUACAO_LABEL[vinculo?.situacao] || 'Ativo';
    const validade = vinculo?.validoAte ? ` · até ${formatDate(vinculo.validoAte)}` : '';
    return `${papel} · ${situacao}${validade}`;
};

/** Um vínculo por licença escolhida na criação, com o papel escolhido para ela. */
export const vinculosDaCriacao = (licenses = [], papeis = {}) => licenses
    .map(normalizeReferenceId)
    .filter(Boolean)
    .map((licenseId) => ({licenseId, papel: papeis[licenseId] || 'USUARIO'}));

export const normalizeRoleLabel = (role) => role?.roleName || role?.nome || role?.name || role?.description || role?.descricao || 'Perfil';

/**
 * Licença a que o perfil pertence: todo perfil é de uma licença só. A lista de perfis
 * (GET /auth/roles) traz `license.id`; a leitura de usuário traz `licenseId`.
 */
export const roleLicenseId = (role) => role?.license?.id || role?.licenseId || role?.license_id || '';

/** Perfis de uma licença, para o seletor dela no cadastro. */
export const perfisDaLicenca = (roles = [], licenseId) => roles.filter((role) => roleLicenseId(role) === licenseId);

/** Ids dos perfis escolhidos, só das licenças que continuam escolhidas. */
export const perfisDaCriacao = (licenses = [], perfisPorLicenca = {}) => [...new Set(licenses
    .map(normalizeReferenceId)
    .filter(Boolean)
    .flatMap((licenseId) => (perfisPorLicenca[licenseId] || []).map(normalizeReferenceId))
    .filter(Boolean))];

export const normalizeLicenseLabel = (license) => {
    const customer = license?.customer?.legalName || license?.empresa?.razao_social || license?.empresa?.nome || 'Empresa';
    const plan = license?.plan?.productPlanName || license?.plano?.nome_plano || 'Plano';
    return `${customer} - ${plan}`;
};

// Para quem não é da equipe Busifarm, `active` e `blocked` já vêm como a situação na assinatura:
// a da conta somada à do vínculo (BBF-67).
const normalizeStatus = (item) => {
    if (item?.blocked === true) return 'Bloqueado';
    if (item?.active === false || item?.enabled === false) return 'Inativo';
    return 'Ativo';
};

const normalizePerfis = (roles = []) => (Array.isArray(roles) ? roles : [])
    .filter(Boolean)
    .map((role) => ({id: normalizeReferenceId(role), nome: normalizeRoleLabel(role), licenseId: roleLicenseId(role)}));

export const normalizeUserRows = (items = []) => items.map((item) => {
    const vinculos = Array.isArray(item?.vinculos) ? item.vinculos : [];
    const licencasVisiveis = new Set(vinculos.map((vinculo) => vinculo?.licenseId));
    return {
        id: item?.userId || item?.id,
        name: item?.username || item?.userName || item?.nome || item?.name || '-',
        email: item?.email || '-',
        // O identity devolve todos os perfis da conta, inclusive de assinaturas de outro cliente.
        // Aqui ficam só os das licenças dos vínculos que esta tela mostra.
        perfis: normalizePerfis(item?.roles).filter((perfil) => (
            licencasVisiveis.size === 0 || !perfil.licenseId || licencasVisiveis.has(perfil.licenseId)
        )),
        vinculos,
        status: normalizeStatus(item),
        raw: item,
    };
});

export const buildUserStats = (rows = []) => ({
    total: rows.length,
    active: rows.filter((row) => row.status === 'Ativo').length,
    inactive: rows.filter((row) => row.status === 'Inativo').length,
    blocked: rows.filter((row) => row.status === 'Bloqueado').length,
});

/** Iniciais para o avatar: duas palavras dão uma letra de cada; uma palavra dá as duas primeiras. */
export const initialsOf = (name = '') => {
    const parts = String(name).trim().split(/[\s._-]+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};
